import { useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import SegmentedTabs from "@/components/base/SegmentedTabs";
import EmptyState from "@/components/base/EmptyState";
import ContentCard from "@/components/feature/ContentCard";
import { searchContent, sortByPopularity, allContent } from "@/lib/content";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);
  const [type, setType] = useState("all");

  usePageMeta({
    title: query ? `“${query}”的搜索结果 - 星空动漫` : "站内搜索 - 星空动漫",
    description:
      "在星空动漫中搜索动漫资讯、作品推荐与剧情解析，支持按内容类型筛选，快速定位你感兴趣的内容。",
    keywords: "站内搜索,动漫搜索,作品搜索,星空动漫",
  });

  const results = useMemo(() => searchContent(query, { type }), [query, type]);
  const hotRecommend = useMemo(() => sortByPopularity(allContent).slice(0, 4), []);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value) {
      setSearchParams({ q: value });
    } else {
      setSearchParams({});
    }
  };

  return (
    <SiteLayout>
      <div className="mx-auto max-w-page px-4 py-8 md:px-6">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索结果" }]} />

        <div className="mt-4 rounded-lg border border-background-200 bg-gradient-to-r from-primary-50 to-background-50 p-6 md:p-8">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">站内搜索</h1>
          <p className="mt-2 text-sm text-foreground-600">
            输入关键词即可搜索全站的资讯、作品与剧情解析。
          </p>
          <form onSubmit={handleSubmit} className="mt-5 max-w-2xl">
            <div className="flex flex-col gap-3 sm:flex-row">
              <label className="flex flex-1 items-center gap-2 rounded-md border border-background-200 bg-background-50 px-4 py-2.5 transition-colors focus-within:border-primary-300">
                <i className="ri-search-line text-base text-foreground-400" />
                <input
                  type="search"
                  name="q"
                  value={keyword}
                  onChange={(event) => setKeyword(event.target.value)}
                  placeholder="试试搜索：星轨彼端 / 深海夜航 / 剧情解析"
                  className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none"
                />
              </label>
              <button
                type="submit"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </div>
          </form>
        </div>

        {query ? (
          <>
            <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-sm text-foreground-600">
                关键词“<strong className="text-primary-600">{query}</strong>” 共找到{" "}
                <strong className="text-primary-600">{results.length}</strong> 条结果
              </p>
              <SegmentedTabs
                value={type}
                onChange={setType}
                segments={[
                  { value: "all", label: "全部" },
                  { value: "news", label: "资讯" },
                  { value: "work", label: "作品" },
                  { value: "analysis", label: "解析" },
                ]}
              />
            </div>

            <div className="mt-6">
              {results.length > 0 ? (
                <div className="flex flex-col gap-4">
                  {results.map((item) => (
                    <ContentCard key={item.id} item={item} variant="row" />
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon="ri-search-eye-line"
                  title="没有找到相关内容"
                  description="试试更换关键词，或浏览下方的热门内容推荐。"
                  actionLabel="清空关键词"
                  onAction={() => {
                    setKeyword("");
                    setSearchParams({});
                  }}
                />
              )}
            </div>
          </>
        ) : (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-12 text-center">
            <i className="ri-search-line text-4xl text-foreground-300" />
            <p className="mt-3 text-base font-semibold text-foreground-900">还没有输入关键词</p>
            <p className="mt-1 text-sm text-foreground-500">
              在上方输入框输入作品名、作者或主题，开始你的探索。
            </p>
          </div>
        )}

        <section className="mt-12">
          <div className="mb-5 flex items-center gap-3">
            <span className="h-5 w-1 rounded-full bg-accent-500" />
            <h2 className="text-xl font-bold text-foreground-950">热门内容推荐</h2>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {hotRecommend.map((item) => (
              <ContentCard key={item.id} item={item} variant="col" />
            ))}
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/works"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
            >
              浏览全部作品
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}